import {
  Dispatch,
  MutableRefObject,
  PropsWithChildren, SetStateAction, createContext, useContext, useMemo,
  useState,
} from 'react';
import { defaultGuid } from '@equifinance/shared';
import { TasksRefNames } from '../../components/Tasks/TasksSidebar';
import {
  UploadTaskDocumentPayload,
  PostTaskNotePayload,
  PostWrittenEvidencePayload,
  Tasks,
  useTasks,
  TaskType,
  UnderwriterDecisionPayload,
} from '.';

export type TasksContextType = {
  id: string,
  tasks: Tasks,
  taskTypes: TaskType[];
  expandedStates: Record<string, boolean>,
  setTasksData: Dispatch<SetStateAction<Tasks>>,
  updateTasks(field: string, value: any, disableIncrementUpdateCount?: boolean): Promise<void>,
  fetchTasks(): Promise<void>,
  createTask(): Promise<void>,
  uploadTaskDocuments(documents: UploadTaskDocumentPayload): Promise<Tasks>,
  handleToggleAll: (expanded: boolean) => void,
  handleToggle: (title: string) => void
  setExpandedState: (title: string, expanded: boolean) => void
  postNote(note: PostTaskNotePayload): Promise<Tasks>,
  createWrittenEvidence(writtenEvidence: PostWrittenEvidencePayload): Promise<Tasks>,
  readTask(taskId: string): Promise<boolean>,
  cancelTask(taskId: string): Promise<Tasks>,
  unklockTask(taskId: string): Promise<Tasks>,
  submitToLender(taskId: string): Promise<void>,
  underwriterSubmission(underwriterDecision: UnderwriterDecisionPayload): Promise<void>,
  dirtyTaskIdsRef: MutableRefObject<Set<string>>
}

export const defaultTasks: Tasks = {
  updateCount: 0,
  dirtyCount: 0,

  id: defaultGuid,
  tasks: [],
  errors: {},
};

const defaultTasksContext: TasksContextType = {
  id: defaultGuid,
  tasks: defaultTasks,
  taskTypes: [],
  expandedStates: {},
  setTasksData: () => { },
  updateTasks: async () => { },
  fetchTasks: async () => { },
  createTask: async () => { },
  uploadTaskDocuments: async () => defaultTasks,
  handleToggleAll: () => { },
  handleToggle: () => { },
  setExpandedState: () => { },
  postNote: async () => defaultTasks,
  createWrittenEvidence: async () => defaultTasks,
  readTask: async () => false,
  cancelTask: async () => defaultTasks,
  unklockTask: async () => defaultTasks,
  submitToLender: async () => { },
  underwriterSubmission: async () => { },
  dirtyTaskIdsRef: {
    current: new Set(),
  },
};

export const TasksContext = createContext<TasksContextType>(defaultTasksContext);

export function useTasksContext() {
  return useContext(TasksContext);
}

export function TasksProvider({ id, children }: PropsWithChildren<{ id: string }>) {
  const tasks = useTasks(id);

  // All sections expand by default
  // Ignore task row
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    Object.values(TasksRefNames).reduce((acc, title) => ({
      ...acc,
      [title]: true,
    }), {}),
  );

  // Expand all/Collapsed work on all sections, and task row
  const handleToggleAll = (expanded: boolean) => {
    const sections = Object.values(TasksRefNames);
    const taskRows = tasks?.tasks?.tasks?.map((task) => task.id) || [];
    const newStates = [...sections, ...taskRows].reduce((acc, title) => ({
      ...acc,
      [title]: expanded,
    }), {});
    setExpandedStates(newStates);
  };

  const handleToggle = (title: string) => {
    setExpandedStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const setExpandedState = (title: string, expanded: boolean = false) => {
    setExpandedStates((prev) => ({
      ...prev,
      [title]: expanded,
    }));
  };

  const contextValue = useMemo(() => ({
    ...tasks,
    expandedStates,
    handleToggleAll,
    handleToggle,
    setExpandedState,
    id,
  }), [id, tasks, expandedStates]);

  return (
    <TasksContext.Provider value={contextValue}>{children}</TasksContext.Provider>
  );
}
